import { SortFieldDisplayProps, SortingOptionsType } from '@components/design/migrated/Table';
import { cloneDeep } from 'lodash';
import React, { useContext } from 'react';
import { DefaultSortingOptionsForCategoryLeaderboard } from '@models/CategoryLeaderboard';
import { OpaSubscription } from '@models/Opa';
import { DefaultSortingOptionsForOpportunityReview } from '@models/OpportunityReview';
import { DefaultSortingOptionsForProducts } from '@models/Products';
import {
  AllSavedFilters,
  Country,
  CountryFilterPair,
  CountryMasterPgsFilterPair,
  Option,
  PageName,
  PageType,
  PageTypeAndName,
  ProductGroupFeatureValue,
  ProductGroupPriceBandValue,
  ProductGroups,
  SavedFilters,
  SelectedFilters,
  CurrencyExchangeRatePerCountry,
} from './Types';
import {
  DefaultSortByOptionsForPerformanceAnalysisStackedChart,
  DefaultSortingOptionsForPerformanceAnalysisboard,
} from '@models/PerformanceAnalysisboard';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';

export const appliedFiltersInitialState = [
  { pageType: PageType.CATEGORY_LEADERBOARD, selectedFilters: [] },
  { pageType: PageType.RETAILER_OVERVIEW, selectedFilters: [] },
  { pageType: PageType.OPPORTUNITY_REVIEW, selectedFilters: [] },
  { pageType: PageType.PERFORMANCE_ANALYSIS, selectedFilters: [] },
  { pageType: PageType.RI_PRODUCT, selectedFilters: [] },
  { pageType: PageType.TRADING_ASSISTANT, selectedFilters: [] },
  { pageType: PageType.EXPLORE, selectedFilters: [] },
];

export interface PagesConfig {
  clientSubscription: OpaSubscription;
  setClientSubscription: any;
  clientId: number;
  setClientId: any;
  clientCountries: Country[];
  setClientCountries: any;
  userId: string;
  setUserId: any;
  pageType: PageType;
  setPageType: any;
  pageName: PageName;
  setPageName: (pageName: PageName) => void;
  getPageTypeAndName: () => PageTypeAndName;
  selectedPgUpdated: boolean; // to check if a diff pg has been selected in pg overview
  setSelectedPgUpdated: any;
  filtersTrayOpened: boolean; // is the filter tray open
  toggleFiltersTray: any;
  selectedFilters: SelectedFilters[]; // the selected filters in the filter tray (not applied yet)
  setSelectedFilters: any;
  appliedFilters: SelectedFilters[]; // the applied filters
  setAppliedFilters: any;
  pgsByPage: ProductGroups[];
  setPgsByPage: any;
  currencyExchangeRatePerCountry: CurrencyExchangeRatePerCountry[];
  updateCurrencyExchangeRatePerCountry: any;
  pgsByCountry: CountryFilterPair[];
  setPGsByCountry: any;
  masterPgsByCountry: CountryMasterPgsFilterPair[];
  setMasterPgsByCountry: any;
  pgsByAppliedCountry: CountryFilterPair[];
  setPGsByAppliedCountry: (pgsByAppliedCountry: CountryFilterPair[]) => void;
  clearFilters: boolean; // clear button in filter tray
  setClearFilters: any;
  applyFilters: boolean; //apply button in filter tray
  setApplyFilters: any;
  addFeatures: any;
  setAddFeatures: any;
  segFeatures: any;
  setSegFeatures: any;
  sortingOptions: SortingOptionsType;
  setSortingOptions: any;
  selectedTimeFrame: any;
  setSelectedTimeFrame: any;
  selectedTimePeriod: any;
  setSelectedTimePeriod: any;
  isNavigateFromCategoryLeaderboard: boolean;
  setNavigateFromCategoryLeaderboard: any;
  pgListUpdated: boolean;
  setPgListUpdated: any;
  savedFilters: AllSavedFilters[];
  setSavedFilters: any;
  isNavigateFromRS: boolean;
  setIsNavigateFromRS: any;
  isNavigateFromOpportunity: boolean;
  setIsNavigateFromOpportunity: any;
  isFirstVisit: boolean;
  setIsFirstVisit: any;
  isCountryChanged: boolean;
  setIsCountryChanged: any;
  isMultiCountryDropdown: boolean;
  setIsMultiCountryDropdown: any;
  sortingOptionsOpportunityReview: SortingOptionsType;
  setSortingOptionsOpportunityReview: any;
  sortingOptionsPerformanceAnalysisStackedChart: SortingOptionsType;
  setSortingOptionsPerformanceAnalysisStackedChart: any;
  isNavigateFromPerfAnalysis: boolean;
  setIsNavigateFromPerfAnalysis: (b: boolean) => void;
  isTimePeriodFirstLoad: boolean;
  setIsTimePeriodFirstLoad: (b: boolean) => void;
  sortingOptionsProducts: SortingOptionsType;
  setSortingOptionsProducts: any;
  isRetailerChanged: boolean;
  setIsRetailerChanged: any;
  hitlistsClientSubscription: OpaSubscription;
  setHitlistsClientSubscription: any;
  hitlistsClientId: number;
  setHitlistsClientId: any;
  hitlistsClientCountries: Country[];
  setHitlistsClientCountries: any;
  productGroupFeatureValueList: ProductGroupFeatureValue[];
  setProductGroupFeatureValueList: (productGroupFeatureValueList: ProductGroupFeatureValue[]) => void;
  productGroupPriceBandValueList: ProductGroupPriceBandValue[];
  setProductGroupPriceBandValueList: (productGroupPriceBandValueList: ProductGroupPriceBandValue[]) => void;
  savedFilterCountryList: string[];
  setSavedFilterCountryList: (savedFilterCountryList: string[]) => void;
  savedFilterToShare: SavedFilters;
  setSavedFilterToShare: (savedFilterToShare: SavedFilters) => void;
  retailKadClientSubscription: OpaSubscription;
  setRetailKadClientSubscription: (retailKadSubscription: OpaSubscription) => void;
  retailKadClientId: number;
  setRetailKadClientId: (clientId: number) => void;
  retailKadClientCountries: Country[];
  setRetailKadClientCountries: (clientCountries: Country[]) => void;
  marketPremiumClientSubscription: OpaSubscription;
  setMarketPremiumClientSubscription: (subscription: OpaSubscription) => void;
  marketPremiumClientId: number;
  setMarketPremiumClientId: (clientId: number) => void;
  marketPremiumClientCountries: Country[];
  setMarketPremiumClientCountries: (clientCountries: Country[]) => void;
  sellerInsightsClientSubscription: OpaSubscription;
  setSellerInsightsClientSubscription: (subscription: OpaSubscription) => void;
  sellerInsightsClientId: number;
  setSellerInsightsClientId: (clientId: number) => void;
  sellerInsightsClientCountries: Country[];
  setSellerInsightsClientCountries: (clientCountries: Country[]) => void;
  sortByOptionsPerformanceAnalysisStackedChart: SortFieldDisplayProps;
  setSortByOptionsPerformanceAnalysisStackedChart: (
    sortByOptionsPerformanceAnalysisStackedChart: SortFieldDisplayProps,
  ) => void;
  isMultiCountryFeatureFlagPA: boolean;
  setIsMultiCountryFeatureFlagPA: (featureFlagEnabled: boolean) => void;
  isSideFiltersFeatureFlagEnabled: boolean;
  setIsSideFiltersFeatureFlagEnabled: (featureFlagEnabled: boolean) => void;
  multiCountryClientCountries: Option[];
  setMultiCountryClientCountries: (clientCountries: Option[]) => void;
  isClearAllFilterTriggered: boolean;
  setIsClearAllFilterTriggered: (isClearAllFilterTriggered: boolean) => void;
  isHidePGInfoWarning: boolean;
  setIsHidePGInfoWarning: (isHidePGInfoWarning: boolean) => void;
  isAllPgsByPeriodicityAvailable: boolean;
  setIsAllPgsByPeriodicityAvailable: (isAllPgsByPeriodicityAvailable: boolean) => void;
  access: Object;
  setAccess: (access: Object) => void;
}

export const initialState: PagesConfig = {
  clientSubscription: { result: { access: 'restricted', restrictions: [] } },
  setClientSubscription: () => {
    return null;
  },
  clientId: 148,
  setClientId: () => {
    return null;
  },
  clientCountries: [],
  setClientCountries: () => {
    return null;
  },
  userId: '',
  setUserId: () => {
    return null;
  },
  pageType: PageType.RETAILER_OVERVIEW,
  setPageType: () => {
    return null;
  },
  pageName: PageName.RETAILER_OVERVIEW,
  setPageName: () => {},
  getPageTypeAndName: () => ({
    pageType: PageType.RETAILER_OVERVIEW,
    pageName: PageName.RETAILER_OVERVIEW,
  }),
  selectedPgUpdated: false,
  setSelectedPgUpdated: () => {
    return null;
  },
  filtersTrayOpened: false,
  toggleFiltersTray: () => {
    return null;
  },
  selectedFilters: cloneDeep(appliedFiltersInitialState),
  setSelectedFilters: () => {
    return null;
  },
  appliedFilters: cloneDeep(appliedFiltersInitialState),
  setAppliedFilters: () => {
    return null;
  },
  pgsByPage: [
    { pageType: PageType.CATEGORY_LEADERBOARD, option: [] },
    { pageType: PageType.RETAILER_OVERVIEW, option: [] },
    { pageType: PageType.OPPORTUNITY_REVIEW, option: [] },
    { pageType: PageType.PERFORMANCE_ANALYSIS, option: [] },
    { pageType: PageType.RI_PRODUCT, option: [] },
    { pageType: PageType.TRADING_ASSISTANT, option: [] },
    { pageType: PageType.EXPLORE, option: [] },
  ],
  setPgsByPage: () => {
    return null;
  },
  currencyExchangeRatePerCountry: [],
  updateCurrencyExchangeRatePerCountry: () => {
    return null;
  },
  pgsByCountry: [],
  setPGsByCountry: () => {
    return null;
  },
  pgsByAppliedCountry: [],
  setPGsByAppliedCountry: () => {
    return null;
  },
  clearFilters: true,
  setClearFilters: () => {
    return null;
  },
  applyFilters: false,
  setApplyFilters: () => {
    return null;
  },
  addFeatures: undefined,
  setAddFeatures: () => {
    return null;
  },
  segFeatures: undefined,
  setSegFeatures: () => {
    return null;
  },
  sortingOptions: DefaultSortingOptionsForCategoryLeaderboard,
  setSortingOptions: () => {
    return null;
  },
  selectedTimeFrame: undefined,
  setSelectedTimeFrame: () => {
    return null;
  },
  selectedTimePeriod: undefined,
  setSelectedTimePeriod: () => {
    return null;
  },
  isNavigateFromCategoryLeaderboard: false,
  setNavigateFromCategoryLeaderboard: () => {
    return null;
  },
  pgListUpdated: false,
  setPgListUpdated: () => {
    return null;
  },
  savedFilters: [
    { pageType: PageType.CATEGORY_LEADERBOARD, savedFilters: [] },
    { pageType: PageType.RETAILER_OVERVIEW, savedFilters: [] },
    { pageType: PageType.OPPORTUNITY_REVIEW, savedFilters: [] },
    { pageType: PageType.PERFORMANCE_ANALYSIS, savedFilters: [] },
    { pageType: PageType.RI_PRODUCT, savedFilters: [] },
    { pageType: PageType.TRADING_ASSISTANT, savedFilters: [] },
    { pageType: PageType.EXPLORE, savedFilters: [] },
  ],
  setSavedFilters: () => {
    return null;
  },
  isNavigateFromRS: false,
  setIsNavigateFromRS: () => {
    return null;
  },
  isNavigateFromOpportunity: false,
  setIsNavigateFromOpportunity: () => {
    return null;
  },
  isFirstVisit: false,
  setIsFirstVisit: () => {
    return null;
  },
  isCountryChanged: false,
  setIsCountryChanged: () => {
    return null;
  },
  isMultiCountryDropdown: false,
  setIsMultiCountryDropdown: () => {
    return null;
  },
  sortingOptionsOpportunityReview: DefaultSortingOptionsForOpportunityReview,
  setSortingOptionsOpportunityReview: () => {
    return null;
  },
  sortingOptionsPerformanceAnalysisStackedChart: DefaultSortingOptionsForPerformanceAnalysisboard,
  setSortingOptionsPerformanceAnalysisStackedChart: () => {
    return null;
  },
  isNavigateFromPerfAnalysis: false,
  setIsNavigateFromPerfAnalysis: () => {
    return null;
  },
  isTimePeriodFirstLoad: true,
  setIsTimePeriodFirstLoad: () => {
    return null;
  },
  sortingOptionsProducts: DefaultSortingOptionsForProducts,
  setSortingOptionsProducts: () => {
    return null;
  },
  isRetailerChanged: true,
  setIsRetailerChanged: () => {
    return null;
  },
  hitlistsClientSubscription: { result: { access: 'restricted', restrictions: [] } },
  setHitlistsClientSubscription: () => {
    return null;
  },
  hitlistsClientId: 148,
  setHitlistsClientId: () => {
    return null;
  },
  hitlistsClientCountries: [],
  setHitlistsClientCountries: () => {
    return null;
  },
  productGroupFeatureValueList: [],
  setProductGroupFeatureValueList: () => {
    return null;
  },
  productGroupPriceBandValueList: [],
  setProductGroupPriceBandValueList: () => {
    return null;
  },
  savedFilterCountryList: [],
  setSavedFilterCountryList: () => {
    return null;
  },
  savedFilterToShare: {
    id: '1',
    name: '',
    filters: [],
    permissions: [],
    namespace: '',
    createdAt: new Date().getTime(),
    clientId: 1,
    country: '',
    periodicity: Periodicity.Monthly,
  },
  setSavedFilterToShare: () => {
    return null;
  },
  marketPremiumClientSubscription: { result: { access: 'restricted', restrictions: [] } },
  setMarketPremiumClientSubscription: () => {
    return null;
  },
  marketPremiumClientId: 148,
  setMarketPremiumClientId: () => {
    return null;
  },
  marketPremiumClientCountries: [],
  setMarketPremiumClientCountries: () => {
    return null;
  },
  sellerInsightsClientSubscription: { result: { access: 'restricted', restrictions: [] } },
  setSellerInsightsClientSubscription: () => {
    return null;
  },
  sellerInsightsClientId: 185590,
  setSellerInsightsClientId: () => {
    return null;
  },
  sellerInsightsClientCountries: [],
  setSellerInsightsClientCountries: () => {
    return null;
  },
  retailKadClientSubscription: { result: { access: 'restricted', restrictions: [] } },
  setRetailKadClientSubscription: () => {},
  retailKadClientId: 148,
  setRetailKadClientId: () => {},
  retailKadClientCountries: [],
  setRetailKadClientCountries: () => {},
  sortByOptionsPerformanceAnalysisStackedChart: DefaultSortByOptionsForPerformanceAnalysisStackedChart,
  setSortByOptionsPerformanceAnalysisStackedChart: () => {
    return null;
  },
  isMultiCountryFeatureFlagPA: false,
  setIsMultiCountryFeatureFlagPA: () => {},
  isSideFiltersFeatureFlagEnabled: false,
  setIsSideFiltersFeatureFlagEnabled: () => {},
  multiCountryClientCountries: [],
  setMultiCountryClientCountries: () => {
    return null;
  },

  isClearAllFilterTriggered: false,
  setIsClearAllFilterTriggered: () => {},
  isHidePGInfoWarning: false,
  setIsHidePGInfoWarning: () => {},
  masterPgsByCountry: [],
  setMasterPgsByCountry: () => {
    return null;
  },
  isAllPgsByPeriodicityAvailable: false,
  setIsAllPgsByPeriodicityAvailable: () => {},
  access: {},
  setAccess: () => {},
};

export const PagesConfigContext = React.createContext<PagesConfig>(initialState);

export const usePageConfig = (): PagesConfig => {
  return useContext(PagesConfigContext);
};
